import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';
import { LynkSeal } from '../../../components/ui/LynkLogo';

/**
 * Closing call — set as a colophon.
 *
 * A colophon is the note at the end of a book saying who made it and in what
 * types. It is the one place in this whole page where a dark ground belongs:
 * the page has been parchment throughout, so a single grenat block at the very
 * end reads as an ending rather than as a second theme. Gilt works here too,
 * which it cannot anywhere on the light surface — 2.1:1 on parchment.
 *
 * The ground is the RAW brand constant `lynk-grenat`, not the semantic
 * `bg-primary`, and that distinction matters. In dark mode `--color-primary`
 * is the lifted claret #C9756B — correct for text and small fills on Night,
 * but as a full-bleed panel it turned this whole block salmon. Like chrome,
 * this surface is meant to be identical in both themes, so it takes the
 * constant and pairs it with creme — the same creme every reversed mark uses.
 *
 * The seal rather than the lockup, because at this size the lockup's tagline
 * rule would collapse, and because a colophon is stamped, not signed.
 */
const CTASection = ({ onGetStarted, onSignIn, isAuthenticated }) => {
  const { t } = useTranslation('conferences');

  return (
    <section className="bg-lynk-grenat text-chrome-foreground">
      <div className="page-shell px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-2xl mx-auto text-center">
          <div className="flex justify-center">
            <LynkSeal size={44} variant="gilt" />
          </div>

          {/* Rules in the reversed ink, at low opacity — the same double rule
              that opened the page, closing it. */}
          <div aria-hidden="true" className="mt-8 mb-1 h-px bg-chrome-foreground/25" />
          <div aria-hidden="true" className="mb-8 h-px bg-chrome-foreground/25" />

          <h2 className="font-heading font-bold text-[26px] sm:text-[32px] leading-[1.15]">
            {t('landing.cta.title')}
          </h2>
          <p className="mt-4 font-academic text-[17px] leading-[1.7] text-chrome-foreground/75">
            {t('landing.cta.subtitle')}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
            {/* On a grenat ground the primary button would vanish into it, so
                the main action inverts: creme fill, grenat ink. Both are
                constants for the same reason the section is. */}
            <Button
              size="lg"
              onClick={onGetStarted}
              className="bg-chrome-foreground text-lynk-grenat hover:bg-lynk-vellum focus-ring-chrome"
            >
              {isAuthenticated ? t('landing.cta.createConference') : t('landing.cta.getStarted')}
            </Button>
            {!isAuthenticated && (
              <Button
                variant="ghost"
                size="lg"
                onClick={onSignIn}
                className="border border-chrome-foreground/30 text-chrome-foreground hover:bg-chrome-foreground/10 hover:text-chrome-foreground focus-ring-chrome"
              >
                {t('landing.cta.signIn')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
